/**
 * KOAI Ephemeral File Upload Component
 * 임시 파일 업로드 UI - 파일은 처리 후 즉시 삭제됨을 안내
 */

import { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  LinearProgress,
  Alert,
  Paper,
  Tooltip,
  Stack
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as DocIcon,
  Close as CloseIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ephemeralAPI } from '../../services/api';

export default function EphemeralFileUpload({ sessionId, onUploadComplete, documents = [] }) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length || !sessionId) return;

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const response = await ephemeralAPI.upload(files, sessionId, {
        onProgress: (job) => {
          setUploadProgress(typeof job.progress === 'number' ? job.progress : 0);
        }
      });
      const data = response.data;

      if (data.success) {
        onUploadComplete?.(data.results);
      } else {
        setError(data.error || '파일 업로드에 실패했습니다.');
      }
    } catch (err) {
      let message = '파일 업로드 중 오류가 발생했습니다.';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed.error) message = parsed.error;
        } catch (_) { /* ignore */ }
      } else if (err.message) {
        message = err.message;
      }
      setError(message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // 파일 입력 초기화
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveDocument = async (documentId) => {
    try {
      await ephemeralAPI.removeDocument(sessionId, documentId);
      onUploadComplete?.(); // 부모에게 갱신 알림
    } catch (err) {
      setError('문서 제거에 실패했습니다.');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  return (
    <Box sx={{ px: 2, pb: 1 }}>
      {/* 안내 메시지 */}
      <Alert 
        severity="info" 
        icon={<InfoIcon fontSize="small" />}
        sx={{ mb: 1, py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}
      >
        파일은 AI 분석 후 즉시 삭제됩니다. 서버에 저장되지 않습니다.
      </Alert>

      {/* 업로드 버튼 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.md,.hwp,.hwpx"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          disabled={uploading}
        />
        <Tooltip title="문서 업로드 (PDF, DOCX, XLSX, TXT, HWP 등)">
          <IconButton
            color="primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            size="small"
          >
            <UploadIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="caption" color="text.secondary">
          문서를 업로드하면 AI가 내용을 분석하여 답변에 활용합니다
        </Typography>
      </Box>

      {/* 업로드 진행 */}
      {uploading && (
        <Box sx={{ mb: 1 }}>
          <LinearProgress variant="indeterminate" sx={{ borderRadius: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            문서 처리 중... (텍스트 추출 및 AI 인덱싱)
          </Typography>
        </Box>
      )}

      {/* 에러 메시지 */}
      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 업로드된 문서 목록 */}
      {documents.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
          {documents.map((doc) => (
            <Chip
              key={doc.id}
              icon={<DocIcon sx={{ fontSize: 16 }} />}
              label={`${doc.name} (${doc.chunkCount}청크)`}
              size="small"
              variant="outlined"
              color="primary"
              onDelete={() => handleRemoveDocument(doc.id)}
              deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              sx={{ fontSize: '0.7rem', maxWidth: 200 }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
